import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useAuth as useClerkAuth, useClerk } from "@clerk/react";

import { api, ApiError } from "./api.js";
import { clearTokenGetter, setTokenGetter } from "./session-token.js";

/**
 * How long to wait for Clerk before admitting it is not coming.
 *
 * Clerk's script is fetched from Clerk's own CDN. If that request fails — a
 * venue with a captive portal, a corporate proxy, an ad blocker, a Clerk outage,
 * or a browser that simply blocks it — then `useClerk().isLoaded` never becomes
 * true, and `loading` below stays true for as long as the tab is open.
 *
 * That failure mode was the worst one available: the host sees a spinner
 * forever. No error, no retry, no way to tell that the problem is sign-in rather
 * than the page, and no way to get to the dashboard, the moderation queue or
 * billing. The public pages are unaffected because they do not wait for Clerk,
 * which makes it worse — the rest of the app works, so a spinner on the host
 * screen reads as a broken product rather than a blocked third party.
 *
 * Ten seconds is long enough to cover a slow but working CDN, since the wait is
 * only ever about a script fetch. Past it we stop pretending to be loading.
 */
const CLERK_LOAD_TIMEOUT_MS = 10_000;

/**
 * Host session state.
 *
 * Clerk decides who is signed in. This context answers a different question:
 * "which Eventgram host is this, and what can they do?" — because the Clerk
 * session token carries no email and no plan, while the dashboard renders the
 * tier and the credit balance that live in D1.
 *
 * So the two are deliberately kept apart. `isSignedIn` comes from Clerk and is
 * authoritative for "should this page render at all"; `host` comes from the
 * Worker and is authoritative for everything the Worker enforces. A host that
 * Clerk has signed in but that has no D1 row yet resolves to null rather than
 * being treated as signed in with a blank profile.
 */

export interface Host {
  readonly email: string;
  readonly displayName: string;
  readonly tier: string;
  readonly credits: number;
}

interface AuthContextValue {
  readonly host: Host | null;
  readonly loading: boolean;
  /** Set when Clerk never finished loading; see CLERK_LOAD_TIMEOUT_MS. */
  readonly clerkUnavailable: boolean;
  /**
   * Set when Clerk signed the user in but the Worker could not answer
   * /api/auth/me for a reason other than "not signed in" — a Clerk outage, a D1
   * failure, a network problem. The UI must show this instead of redirecting to
   * the sign-in page, or a server fault sends the host round a sign-in loop.
   */
  readonly hostError: string | null;
  readonly signOut: () => Promise<void>;
  readonly refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded, getToken, userId } = useClerkAuth();
  const clerk = useClerk();

  const [host, setHost] = useState<Host | null>(null);
  const [loading, setLoading] = useState(true);
  const [clerkUnavailable, setClerkUnavailable] = useState(false);
  const [hostError, setHostError] = useState<string | null>(null);

  /*
   * Which /api/auth/me call is allowed to write state.
   *
   * `refresh` re-runs whenever Clerk's session changes. A slow answer for the
   * previous user can arrive after the new one has already been resolved — on an
   * account switch, on a sign-out, or on a manual retry — and without this
   * guard it would overwrite the newer state with a stale profile.
   */
  const requestId = useRef(0);

  // Register the token getter before `refresh` runs. These effects are declared
  // in order, so this one is installed first; keeping the bridge in a separate
  // child component made the ordering less obvious and let a cold load call
  // /api/auth/me before the getter existed.
  useEffect(() => {
    setTokenGetter(getToken);
    return clearTokenGetter;
  }, [getToken]);

  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    const isCurrent = () => id === requestId.current;

    // Clerk has not rehydrated yet. Do not set `loading` false here: that was
    // the cold-load bug. The first render ran with isLoaded=false, immediately
    // ended the loading state, and RequireHost redirected to /login before
    // Clerk ever had a chance to say the host was signed in.
    if (!isLoaded) {
      if (isCurrent()) setHost(null);
      return;
    }

    if (!isSignedIn) {
      if (!isCurrent()) return;
      setHost(null);
      setLoading(false);
      setClerkUnavailable(false);
      setHostError(null);
      return;
    }

    setHostError(null);
    // A retry from the error screen should look like it is doing something. The
    // ordinary cold load already starts in this state, so this does not flash.
    setLoading(true);

    /*
     * Up to three attempts, only for a 401.
     *
     * `readSessionToken` races Clerk's `getToken()` against a short deadline so
     * a blocked Clerk CDN cannot wedge the public pages. On a cold host load
     * that race is occasionally lost even though Clerk is about to supply a
     * perfectly good token — the request goes out anonymous, the Worker answers
     * 401, and this provider used to treat that as "not a host" and redirect to
     * /login. Waiting a beat and asking again is enough; by the third call Clerk
     * has loaded, and a genuine 401 still fails fast (about one second).
     */
    const attempts = 3;
    let result: { host: Host } | undefined;
    let lastError: unknown;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        result = await api.get<{ host: Host }>("/api/auth/me");
        break;
      } catch (error) {
        lastError = error;

        // Only "no token yet" is worth retrying. A 403 (unverified email), a
        // 503 (Clerk outage) or a D1 failure will not fix itself in 700ms, and
        // the caller has a retry button for those.
        const retryable = error instanceof ApiError && error.status === 401;
        if (!retryable || attempt === attempts) break;
        await new Promise((resolve) => setTimeout(resolve, attempt === 1 ? 300 : 700));
      }
    }

    // A newer refresh owns the state now; this answer is stale.
    if (!isCurrent()) return;

    if (!result) {
      setHost(null);

      if (lastError instanceof ApiError && lastError.status === 401) {
        // A valid Clerk session with no host profile yet. /login explains that
        // and offers sign-out; it is not a server error.
        setHostError(null);
      } else {
        setHostError(describeError(lastError));
      }
      setLoading(false);
      return;
    }

    setHost(result.host);
    setHostError(null);
    setLoading(false);
  }, [isLoaded, isSignedIn, userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * The deadline.
   *
   * This is a timer rather than extra logic in `refresh` on purpose: `refresh`
   * is keyed on Clerk's state and only re-runs when that state changes, so it has
   * no opportunity to notice that time has passed. If Clerk never loads, this is
   * the only thing that will.
   *
   * `loading` stays true while the deadline has not passed, so the ordinary slow
   * path is unchanged and nothing flashes.
   */
  useEffect(() => {
    if (isLoaded) {
      setClerkUnavailable(false);
      return;
    }
    const timer = setTimeout(() => setClerkUnavailable(true), CLERK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoaded]);

  const signOut = useCallback(async () => {
    // Invalidate any /api/auth/me call that is still in flight: its answer is
    // for the session being ended and must not repopulate `host` after this.
    requestId.current += 1;

    // Order matters. Clerk first, so the token this tab would otherwise replay
    // stops existing immediately; clearing our own state second means the UI
    // cannot render a stale profile for a frame.
    try {
      await clerk.signOut();
    } catch (error) {
      /*
       * A failed sign-out leaves the Clerk session alive. Clearing local state
       * anyway would claim the user is signed out when they are not, so log the
       * failure and keep the profile. The button stays available and the user
       * can try again; nothing here widens access.
       */
      console.error(
        JSON.stringify({
          event: "clerk_sign_out_failed",
          error: error instanceof Error ? error.message : String(error),
        }),
      );
      return;
    }

    setHost(null);
    setHostError(null);
  }, [clerk]);

  const value: AuthContextValue = {
    host,
    loading: loading || (!isLoaded && !clerkUnavailable),
    clerkUnavailable,
    hostError,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

/** Message suitable for showing a user, from any thrown value. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Try again.";
}